<template>
  <section class="admin-content" id="hiva-chat">
    <Navbar />
    <main class="admin-main">
      <div class="bg-info m-b-30">
        <div class="container">
          <div class="row p-b-40 p-t-40 align-items-center">
            <div class="col-md-8 text-white">
              <h3 class="h3 text-white m-b-10">
                <i class="fe fe-message-circle m-r-10"></i>
                Chat with HIVA<sup>TM</sup>
              </h3>
              <p class="m-b-0">
                Your AI assistant for quick support on claims, enrollees, referrals, and operations.
              </p>
            </div>
            <div class="col-md-4 text-md-right m-t-15 m-md-t-0">
              <button class="btn btn-light" @click="resetChat" :disabled="sending">
                <i class="fe fe-refresh-cw m-r-5"></i>
                New Chat
              </button>
            </div>
          </div>
        </div>
      </div>

      <section>
        <div class="container">
          <div class="row">
            <div class="col-lg-8 m-b-30">
              <div class="card chat-shell">
                <div class="card-body p-0 d-flex flex-column">
                  <div class="chat-messages" ref="chatBody">
                    <div
                      v-for="(message, index) in messages"
                      :key="`${message.role}-${index}-${message.timestamp}`"
                      class="chat-row"
                      :class="message.role === 'user' ? 'chat-row-user' : 'chat-row-assistant'"
                    >
                      <div class="chat-bubble" :class="message.role === 'user' ? 'chat-user' : 'chat-assistant'">
                        <p class="chat-text" v-if="message.content">{{ message.content }}</p>

                        <div v-if="message.summary" class="chat-summary m-b-10">{{ message.summary }}</div>

                        <div v-if="message.metrics" class="m-b-10">
                          <div class="row">
                            <div
                              class="col-12"
                              v-for="(value, label) in message.metrics"
                              :key="`${label}-${index}`"
                            >
                              <div class="metric-box">
                                <p class="metric-label m-b-0">{{ label }}</p>
                                <h5 class="metric-value m-b-0">{{ value }}</h5>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div v-if="message.chartOptions" class="m-b-10">
                          <highcharts :options="message.chartOptions"></highcharts>
                        </div>

                        <div
                          v-if="message.tableRows && message.tableRows.length"
                          class="table-responsive m-b-10"
                        >
                          <table class="table table-sm table-bordered m-b-0">
                            <thead>
                              <tr>
                                <th v-for="column in message.tableColumns" :key="`${column}-${index}`">
                                  {{ column }}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                v-for="(row, rowIndex) in message.tableRows"
                                :key="`row-${index}-${rowIndex}`"
                              >
                                <td
                                  v-for="column in message.tableColumns"
                                  :key="`col-${index}-${rowIndex}-${column}`"
                                >
                                  {{ formatCellValue(row[column]) }}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        <small
                          v-if="message.visualizationType"
                          class="d-block text-muted m-b-5"
                        >
                          Visualization: {{ message.visualizationType }}
                        </small>

                        <small class="chat-time">{{ formatTime(message.timestamp) }}</small>
                      </div>
                    </div>

                    <div v-if="sending" class="chat-row chat-row-assistant">
                      <div class="chat-bubble chat-assistant">
                        <p class="chat-text m-b-0">
                          HIVA is typing
                          <span class="typing-dots">
                            <span>.</span><span>.</span><span>.</span>
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div class="chat-composer border-top">
                    <div class="p-3">
                      <label class="small text-muted d-block m-b-10">Ask HIVA anything</label>
                      <textarea
                        class="form-control"
                        rows="3"
                        v-model="draft"
                        placeholder="Type your question here..."
                        @keydown.enter.exact.prevent="sendMessage()"
                      ></textarea>
                      <small class="text-muted d-block m-t-10">Press Enter to send, Shift + Enter for new line.</small>

                      <div class="d-flex justify-content-between align-items-center m-t-15 flex-wrap">
                        <span class="text-danger small" v-if="chatError">{{ chatError }}</span>
                        <button class="btn btn-info" @click="sendMessage()" :disabled="!canSend">
                          <i class="fe fe-send m-r-5"></i>
                          Send Message
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-4 m-b-30">
              <div class="card">
                <div class="card-body">
                  <h5 class="m-b-15">Conversation Starters</h5>
                  <p class="text-muted small m-b-15">Tap any suggestion to ask quickly.</p>
                  <button
                    v-for="(suggestion, index) in suggestions"
                    :key="`suggestion-${index}`"
                    class="btn btn-outline-info btn-block text-left m-b-10"
                    @click="sendMessage(suggestion)"
                    :disabled="sending"
                  >
                    <i class="fe fe-zap m-r-5"></i>
                    {{ suggestion }}
                  </button>
                </div>
              </div>

              <div class="card m-t-15">
                <div class="card-body">
                  <h6 class="m-b-10">Tips</h6>
                  <ul class="pl-3 m-b-0 text-muted small">
                    <li>Ask short, specific questions for faster answers.</li>
                    <li>Include enrollee ID or claim number when relevant.</li>
                    <li>Use this chat for guidance; verify final actions in the main modules.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";

let agencyStorage = localStorage.getItem("agency");
let agency = agencyStorage == null ? null : JSON.parse(agencyStorage);

export default {
  components: {
    Navbar,
  },
  data() {
    return {
      user: null,
      agency: agency,
      draft: "",
      sending: false,
      chatError: "",
      suggestions: [
        "How many enrollees do we have?",
        "How many Pregnant women do we have?",
        "How much is requested for claims this current month?",
        "What are the 20 most common diagnoses this month?",
      ],
      messages: [
        {
          role: "assistant",
          content:
            "Hello, I’m HIVA. I can help you navigate claims, enrollee workflows, referrals, and platform operations. What would you like to do today?",
          timestamp: new Date().toISOString(),
        },
      ],
    };
  },
  computed: {
    canSend() {
      return !this.sending && this.draft.trim().length > 0;
    },
  },
  beforeMount() {
    this.user = JSON.parse(localStorage.getItem("user"));
  },
  methods: {
    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    resetChat() {
      this.messages = [
        {
          role: "assistant",
          content:
            "Fresh chat started. Ask me about claims, referrals, enrollee management, or reporting.",
          timestamp: new Date().toISOString(),
        },
      ];
      this.chatError = "";
      this.draft = "";
      this.scrollToBottom();
    },
    async sendMessage(prefilledText = "") {
      const text = (prefilledText || this.draft).trim();
      if (!text || this.sending) {
        return;
      }

      this.chatError = "";
      this.sending = true;

      this.messages.push({
        role: "user",
        content: text,
        timestamp: new Date().toISOString(),
      });

      this.draft = "";
      this.scrollToBottom();

      try {
        const reply = await this.getAssistantReply(text);
        this.messages.push({
          role: "assistant",
          content: reply.content || "",
          summary: reply.summary || "",
          visualizationType: reply.visualizationType || "",
          metrics: reply.metrics || null,
          chartOptions: reply.chartOptions || null,
          tableColumns: reply.tableColumns || [],
          tableRows: reply.tableRows || [],
          timestamp: new Date().toISOString(),
        });
      } catch (error) {
        this.chatError = "HIVA service is currently unavailable. Showing a local helper response.";
        this.messages.push({
          role: "assistant",
          content: this.generateFallbackReply(text),
          timestamp: new Date().toISOString(),
        });
      } finally {
        this.sending = false;
        this.scrollToBottom();
      }
    },
    async getAssistantReply(message) {
      const history = this.messages.slice(-8).map((item) => ({
        role: item.role,
        content: this.getMessageTextForHistory(item),
      }));
      history;

      const payload = {
        query: message,
        state_code: 'osun',
        // history,
        // agency_id: this.agency ? 95930 : null,
        // user_id: this.user ? this.user.id : null,
      };

      const response = await this.axios.post("/api/v1/auth/hiva/query", payload);
      const parsed = this.normalizeAssistantPayload(response.data);
      if (
        parsed.content ||
        parsed.summary ||
        (parsed.tableRows && parsed.tableRows.length) ||
        parsed.metrics
      ) {
        return parsed;
      }

      return {
        content: this.generateFallbackReply(message),
      };
    },
    getMessageTextForHistory(message) {
      if (!message) return "";
      if (message.content) return message.content;
      if (message.summary) return message.summary;
      if (message.metrics) {
        const labels = Object.keys(message.metrics);
        if (labels.length) {
          return labels.map((label) => `${label}: ${message.metrics[label]}`).join(", ");
        }
      }
      return "";
    },
    normalizeAssistantPayload(data) {
      const result = {
        content: "",
        summary: "",
        visualizationType: "",
        metrics: null,
        chartOptions: null,
        tableColumns: [],
        tableRows: [],
      };

      if (!data) {
        return result;
      }

      if (typeof data === "string") {
        result.content = data;
        return result;
      }

      const summary = typeof data.summary === "string" ? this.cleanSummary(data.summary) : "";
      const extracted = this.extractAssistantMessage(data);

      result.summary = summary;
      result.content = extracted;

      const visualization = data.visualization && typeof data.visualization === "object" ? data.visualization : null;
      const responseData = Array.isArray(data.data) ? data.data : [];
      const visualizationData =
        visualization && Array.isArray(visualization.data) ? visualization.data : [];
      const rows = responseData.length ? responseData : visualizationData;

      if (visualization && typeof visualization.type === "string") {
        result.visualizationType = visualization.type;
      }

      if (rows.length) {
        result.tableRows = rows;
      }

      if (visualization && Array.isArray(visualization.columns) && visualization.columns.length) {
        result.tableColumns = visualization.columns;
      } else if (rows.length) {
        result.tableColumns = Object.keys(rows[0]);
      }

      if (result.visualizationType === "metrics" && rows.length && typeof rows[0] === "object") {
        result.metrics = rows[0];
      }

      result.chartOptions = this.buildChartOptions(visualization, rows, result.tableColumns);

      return result;
    },
    buildChartOptions(visualization, rows, columns) {
      if (!rows || !rows.length) {
        return null;
      }

      const visualizationType = visualization && visualization.type
        ? String(visualization.type).toLowerCase()
        : "";
      const tableLikeTypes = ["table", "grid", "list", "metrics", "kpi", "key-value", "key_value"];
      if (tableLikeTypes.includes(visualizationType)) {
        return null;
      }

      const visualizationMeta = visualization && visualization.metadata ? visualization.metadata : {};
      const numericColumns = Array.isArray(visualization && visualization.numeric_columns)
        ? visualization.numeric_columns
        : columns.filter((column) => this.isNumericColumn(rows, column));
      const categoricalColumns = Array.isArray(visualization && visualization.categorical_columns)
        ? visualization.categorical_columns
        : columns.filter((column) => !numericColumns.includes(column));

      if (!numericColumns.length) {
        return null;
      }

      const title = visualization && visualization.type
        ? `${visualization.type.toUpperCase()} Visualization`
        : "HIVA Analysis";
      const preferredChartType = this.getChartTypeFromVisualization(visualizationType);

      const firstCategoryColumn = categoricalColumns[0] || null;

      if (!firstCategoryColumn && rows.length === 1 && numericColumns.length) {
        return {
          chart: {
            type: preferredChartType || "column",
            height: this.getChartHeight(visualizationMeta),
          },
          title: { text: title },
          xAxis: { categories: numericColumns },
          yAxis: { title: { text: "Value" } },
          legend: { enabled: false },
          series: [
            {
              name: "Value",
              data: numericColumns.map((column) => Number(rows[0][column]) || 0),
            },
          ],
          credits: { enabled: false },
        };
      }

      const categories = firstCategoryColumn
        ? rows.map((row, index) => {
            const value = row[firstCategoryColumn];
            return value === undefined || value === null || value === "" ? `Item ${index + 1}` : String(value);
          })
        : rows.map((_, index) => `Item ${index + 1}`);

      const series = numericColumns.map((column) => ({
        name: column,
        data: rows.map((row) => Number(row[column]) || 0),
      }));

      return {
        chart: {
          type: preferredChartType || (numericColumns.length > 1 ? "line" : "column"),
          height: this.getChartHeight(visualizationMeta),
        },
        title: { text: title },
        xAxis: { categories },
        yAxis: { title: { text: "Value" } },
        legend: { enabled: true },
        series,
        credits: { enabled: false },
      };
    },
    getChartTypeFromVisualization(visualizationType) {
      if (!visualizationType) return null;

      if (["trend", "timeseries", "time_series", "line"].includes(visualizationType)) {
        return "line";
      }

      if (["distribution", "bar", "histogram"].includes(visualizationType)) {
        return "bar";
      }

      if (["comparison", "category", "ranking", "rank", "column"].includes(visualizationType)) {
        return "column";
      }

      if (["composition", "pie", "donut", "doughnut"].includes(visualizationType)) {
        return "pie";
      }

      return null;
    },
    getChartHeight(metadata) {
      const settings = metadata && metadata.settings ? metadata.settings : {};
      return typeof settings.height === "number" ? settings.height : 280;
    },
    isNumericColumn(rows, column) {
      return rows.every((row) => {
        const value = row[column];
        if (value === null || value === undefined || value === "") return true;
        return !Number.isNaN(Number(value));
      });
    },
    extractAssistantMessage(data) {
      if (!data) return "";
      if (typeof data === "string") return data;
      if (typeof data.summary === "string") return "";
      if (typeof data.reply === "string") return data.reply;
      if (typeof data.message === "string") return data.message;
      if (data.data && typeof data.data.reply === "string") return data.data.reply;
      if (data.data && typeof data.data.message === "string") return data.data.message;
      if (
        Array.isArray(data.choices) &&
        data.choices[0] &&
        data.choices[0].message &&
        typeof data.choices[0].message.content === "string"
      ) {
        return data.choices[0].message.content;
      }
      return "";
    },
    cleanSummary(text) {
      return text
        .replace(/\*\*/g, "")
        .replace(/\r/g, "")
        .trim();
    },
    formatCellValue(value) {
      if (value === null || value === undefined) return "-";
      if (typeof value === "object") return JSON.stringify(value);
      return value;
    },
    generateFallbackReply(message) {
      const lowerMessage = message.toLowerCase();

      if (lowerMessage.includes("claim")) {
        return "For claims support: go to Claims Manager, open Manage Claims, filter by status/date, then review diagnosis, tariff, and uploaded documents before vetting.";
      }

      if (lowerMessage.includes("enrollee") || lowerMessage.includes("enrollment")) {
        return "For enrollee workflows: use Manage Enrollees to add or update records, verify biodata carefully, and confirm assigned facility before final submission.";
      }

      if (lowerMessage.includes("referral")) {
        return "For referrals: open View Referrals, validate source and destination facilities, then confirm patient details and service reason before approval.";
      }

      if (lowerMessage.includes("payment") || lowerMessage.includes("finance")) {
        return "For finance operations: check Finance Manager dashboards, reconcile claim statuses first, then proceed with payment orders to avoid rejected disbursements.";
      }

      return "I can help with claims, enrollees, referrals, reporting, and operations guidance. Ask me a specific task and I’ll provide a clear step-by-step answer.";
    },
    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.chatBody) {
          this.$refs.chatBody.scrollTop = this.$refs.chatBody.scrollHeight;
        }
      });
    },
  },
};
</script>

<style scoped>
.chat-shell {
  min-height: 600px;
}

.chat-messages {
  height: 460px;
  overflow-y: auto;
  padding: 20px;
  background: #f8f9fb;
}

.chat-row {
  display: flex;
  margin-bottom: 12px;
}

.chat-row-user {
  justify-content: flex-end;
}

.chat-row-assistant {
  justify-content: flex-start;
}

.chat-bubble {
  max-width: 82%;
  border-radius: 12px;
  padding: 12px 14px;
}

.chat-user {
  background: #17a2b8;
  color: #ffffff;
}

.chat-assistant {
  background: #ffffff;
  border: 1px solid #e8ecf3;
}

.chat-text {
  white-space: pre-wrap;
  margin-bottom: 6px;
}

.chat-summary {
  white-space: pre-wrap;
  font-size: 13px;
  line-height: 1.5;
}

.metric-box {
  border: 1px solid;
  border-radius: 10px;
  padding: 10px;
}

.metric-label {
  font-size: 12px;
  opacity: 0.8;
}

.metric-value {
  font-weight: 700;
}

.chat-time {
  opacity: 0.8;
  font-size: 11px;
}

.chat-composer {
  background: #ffffff;
}

.typing-dots span {
  animation: blink 1.2s infinite;
  margin-left: 2px;
}

.typing-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes blink {
  0%,
  80%,
  100% {
    opacity: 0.3;
  }
  40% {
    opacity: 1;
  }
}
</style>
